import React from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { faSliders } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { noop } from "lodash";

import ActionItem from "~/actionItems/ActionItem";
import { SORT_BY_DATE_CREATED, SORT_BY_DUE_DATE } from "~/actionItems/constants";
import type { SortByType } from "~/actionItems/filters/SortBy";
import { ActionItemsContext, ActionItemsState } from "~/actionItems/newActionItems/ActionItemsContext";
import { createdAtSort, dueDateSort } from "~/actionItems/utils";
import { reduceEdgesToNodes } from "~/lib/utils";
import { EmptyState } from "~/meetings/SeriesSidePanel/EmptyState";
import { SidePanelItem } from "~/meetings/SeriesSidePanel/SidePanelItem";
import Avatar from "~/ui/avatar/Avatar";
import { Button, LabelIconForButton } from "~/ui/button";
import Menu from "~/ui/menu";

import { LoadingFallback } from "./LoadingFallback";

import type { ActionItemsPanelQuery, ActionItemsPanelQuery$data } from "~/__generated__/ActionItemsPanelQuery.graphql";

import styles from "./ActionItemsPanel.module.css";

const actionItemsQuery = graphql`
	query ActionItemsPanelQuery(
		$cursor: String
		$count: Int = 1000
		$includeCompleted: Boolean
		$includeWontDo: Boolean
		$includeOnlyCreatedByYou: Boolean
		$includeAiDetected: Boolean
		$assignedTo: [ID!]
		$forStream: ID!
	) {
		actionItems(
			first: $count
			after: $cursor
			includeCompleted: $includeCompleted
			includeWontDo: $includeWontDo
			includeOnlyCreatedByYou: $includeOnlyCreatedByYou
			includeAiDetected: $includeAiDetected
			assignedTo: $assignedTo
			forStream: $forStream
		) @connection(key: "ActionItemsPanel_actionItems") {
			edges {
				node {
					...ActionItemData @relay(mask: false)
				}
			}
		}
	}
`;

type ActionItemData = NonNullable<ActionItemsPanelQuery$data["actionItems"]["edges"][number]>["node"];
type AssigneeData = NonNullable<NonNullable<ActionItemData["assignments"]>[number]>["assignee"];

const groupActionItemsByAssignee = (nodes: ActionItemData[]) => {
	const assigneeMap = new Map<string, { assignee: AssigneeData | null; actionItems: ActionItemData[] }>();
	const unassignedItems: ActionItemData[] = [];

	// Group the nodes by assignee
	nodes.forEach(node => {
		// Skip action items that have an original block, since they are linked action items and we don't want to display those
		if (node.hasOriginalBlock) {
			return;
		}

		if (node.assignments && node.assignments.length > 0) {
			// Iterate through each assignment for this action item
			node.assignments.forEach(assignment => {
				const assigneeId = assignment.assignee.id;

				// Get existing data for this assignee or create new entry
				const existingData = assigneeMap.get(assigneeId) || { assignee: assignment.assignee, actionItems: [] };

				// Add this action item to the assignee's list
				assigneeMap.set(assigneeId, {
					assignee: assignment.assignee,
					actionItems: [...existingData.actionItems, node],
				});
			});
		} else {
			unassignedItems.push(node);
		}
	});

	if (unassignedItems.length > 0) {
		const unassignedKey = "unassigned";
		assigneeMap.set(unassignedKey, {
			assignee: null,
			actionItems: unassignedItems,
		});
	}

	return assigneeMap;
};

function ActionItemAssigneeGroup({
	assignee,
	actionItems,
}: {
	assignee: AssigneeData | null;
	actionItems: ActionItemData[];
}) {
	return (
		<section aria-labelledby={assignee ? `assignee-${assignee.id}` : "assignee-unassigned"}>
			<div className={styles.assigneeContainer}>
				{assignee ? (
					<>
						<Avatar user={assignee} size="small" />
						<h3 id={`assignee-${assignee.id}`} className={styles.assigneeName}>
							{assignee?.fullName}
						</h3>
					</>
				) : (
					<h3 id="assignee-unassigned" className={styles.assigneeName}>
						Unassigned
					</h3>
				)}
				<span className={styles.actionItemCount}>{actionItems.length}</span>
			</div>
			{actionItems.map(item => (
				<ActionItem key={`${item.id}-${assignee?.fullName}`} item={item} adjacentItemSelect={noop} />
			))}
		</section>
	);
}

function ActionItemsList({ streamId }: { streamId: string }) {
	const { filters } = React.useContext(ActionItemsContext);
	const { showCompleted, showWontDo, showAIDetected, sortBy } = filters;

	const { actionItems } = useLazyLoadQuery<ActionItemsPanelQuery>(
		actionItemsQuery,
		{
			forStream: streamId,
			includeCompleted: showCompleted,
			includeWontDo: showWontDo,
			includeAiDetected: showAIDetected,
		},
		{
			fetchPolicy: "store-and-network",
		},
	);
	const actionItemNodes = reduceEdgesToNodes(actionItems.edges);
	const actionItemsByAssignee = React.useMemo(() => groupActionItemsByAssignee(actionItemNodes), [actionItemNodes]);

	const sortedActionItemsByAssignee = React.useMemo(() => {
		let sortingFunction = dueDateSort;
		if (sortBy === SORT_BY_DATE_CREATED) {
			sortingFunction = createdAtSort;
		}

		const sortedMap = new Map();
		actionItemsByAssignee.forEach((value, key) => {
			const sortedActionItems = [...value.actionItems].sort((a, b) => sortingFunction({ node: a }, { node: b }));
			sortedMap.set(key, {
				assignee: value.assignee,
				actionItems: sortedActionItems,
			});
		});

		return sortedMap;
	}, [actionItemsByAssignee, sortBy]);

	if (sortedActionItemsByAssignee.size === 0) {
		return (
			<EmptyState
				title="No action items"
				description="Action items for this meeting series that match your filters will show up here."
			/>
		);
	}

	return (
		<>
			{Array.from(sortedActionItemsByAssignee.entries()).map(([assigneeId, { assignee, actionItems }]) => {
				return <ActionItemAssigneeGroup key={assigneeId} assignee={assignee} actionItems={actionItems} />;
			})}
		</>
	);
}

interface ActionItemsPanelProps {
	streamId: string;
	onClose?: () => void;
}

function ActionItemsPanelContent({ streamId, onClose }: ActionItemsPanelProps) {
	const { filters } = React.useContext(ActionItemsContext);
	const {
		showCompleted,
		onToggleShowCompleted,
		showWontDo,
		onToggleShowWontDo,
		showAIDetected,
		onToggleShowAIDetected,
		sortBy,
		onChangeSortBy,
	} = filters;

	const onSelectSort = React.useCallback((type: SortByType) => () => onChangeSortBy(type), [onChangeSortBy]);

	return (
		<SidePanelItem>
			<SidePanelItem.Header onClose={onClose} headerTitle={<SidePanelItem.Title>Action Items</SidePanelItem.Title>}>
				<div className={styles.headerActions}>
					<Menu>
						<Menu.Trigger>
							<Button variant="secondary" size="slim">
								<LabelIconForButton side="left" icon={faSliders} />
								Filters
							</Button>
						</Menu.Trigger>
						<Menu.Content>
							<Menu.Label>Show</Menu.Label>
							<Menu.SwitchItem checked={showCompleted} onCheckedChange={onToggleShowCompleted}>
								Completed
							</Menu.SwitchItem>
							<Menu.SwitchItem checked={showWontDo} onCheckedChange={onToggleShowWontDo}>
								Archived
							</Menu.SwitchItem>
							<Menu.SwitchItem checked={showAIDetected} onCheckedChange={onToggleShowAIDetected}>
								AI-Detected
							</Menu.SwitchItem>
							<Menu.Separator />
							<Menu.Label>Sort by</Menu.Label>
							<Menu.RadioGroup value={sortBy}>
								<Menu.RadioItem value={SORT_BY_DUE_DATE} onSelect={onSelectSort(SORT_BY_DUE_DATE)}>
									Due date
								</Menu.RadioItem>
								<Menu.RadioItem value={SORT_BY_DATE_CREATED} onSelect={onSelectSort(SORT_BY_DATE_CREATED)}>
									Date created
								</Menu.RadioItem>
							</Menu.RadioGroup>
						</Menu.Content>
					</Menu>
				</div>
			</SidePanelItem.Header>
			<React.Suspense fallback={<LoadingFallback />}>
				<SidePanelItem.Body className={styles.panelContainer}>
					<ActionItemsList streamId={streamId} />
				</SidePanelItem.Body>
			</React.Suspense>
		</SidePanelItem>
	);
}

export function ActionItemsPanel({ streamId, onClose }: ActionItemsPanelProps) {
	return (
		<ActionItemsState renderingContext="seriesPanel">
			<ActionItemsPanelContent streamId={streamId} onClose={onClose} />
		</ActionItemsState>
	);
}
